/**
 * Provider clinical hours for the Huddle page ($/hr) and Advanced Production Trends.
 *
 * ONE implementation of the rule, shared by Huddle.vue (today) and ProductionTrends.vue (date range).
 * Spec + rationale: docs/plans/2026-10-18-provider-clinical-hours-plan.md section "3-v2" (2026-10-19).
 *
 * KEEP THIS FILE IMPORT-FREE so node-tests execute it verbatim
 * (cypress/node-tests/huddle-clinical-hours.test.js strips the `export` keywords and runs it).
 * Same convention as src/utils/followups/listExport.js and phiMask.js.
 *
 * THE RULE, for provider P on day D (the office's calendar day):
 *   appts(P,D)   appointments (status 1/2/5) where P is the row's ProvNum OR ProvHyg; each is [t, t + Pattern x 5 min]
 *   blocks(P,D)  the office's Open Dental provider schedule rows for P on D, MERGED: sorted by start, and any block
 *                that starts at or before the running stop is folded in. Open Dental often keeps duplicate or
 *                stacked rows for one day (Mint prod, 2026-10-19: 2x 09:00-13:30 + 6x 10:00-17:00 = 8 h covered,
 *                not 51), so the time covered is counted once.
 *
 *   no appointments   -> 0 hours, source 'none'   (holiday / stale-template guard; keeps Working Days meaningful)
 *   windowStart       = earlier of (first appointment start, first merged block start)
 *   windowEnd         = later of (latest appointment END over ALL appointments, last merged block stop)
 *   emptyGap          = for each gap BETWEEN two consecutive merged blocks, the minutes no appointment covers; summed
 *   goalsLunch        = Set Goals lunch when P is listed AND the weekday applies, else 0 - there is NO default
 *   lunch             = max(emptyGap, goalsLunch)   - the larger of the two, never both (no double deduction)
 *   hours             = max(0, windowEnd - windowStart - lunch) / 60
 *                       source 'schedule' when P has any schedule block that day, else 'appointments'
 *
 * Clinical hours are PAID PRESENCE, not hours worked: broken appointments and mid-day gaps count.
 * Running past the schedule or starting before it both count. There is no daily cap.
 *
 * Attribution: Open Dental stores a hygiene visit as ProvNum = <dentist>, ProvHyg = <hygienist>, so the
 * hygienist never appears in ProvNum. A row counts toward BOTH. (Same fix as
 * online-scheduling-service/availability-core.js::appointmentProviderNums, 2026-09-04.)
 *
 * Times are office WALL-CLOCK strings ("2026-10-08 08:20:00", "08:00:00"). They are read textually and
 * never turned into a Date, so the viewer's timezone cannot shift them.
 *
 * Run standalone:  node src/utils/huddle/clinicalHours.js   (should print nothing)
 */

/**
 * Distinct provider numbers (> 0) an appointment row occupies, ProvNum first.
 * A row with ProvNum === ProvHyg counts once; ProvHyg 0 / null is ignored; strings are tolerated.
 * Copy of online-scheduling-service/availability-core.js::appointmentProviderNums.
 *
 * @param {Object} row - { ProvNum, ProvHyg }
 * @returns {Array<number>}
 */
export function appointmentProviderNums(row) {
  const nums = [];
  const prov = parseInt((row || {}).ProvNum, 10);
  if (Number.isFinite(prov) && prov > 0) nums.push(prov);
  const hyg = parseInt((row || {}).ProvHyg, 10);
  if (Number.isFinite(hyg) && hyg > 0 && hyg !== prov) nums.push(hyg);
  return nums;
}

/**
 * Minutes from midnight for "HH:MM[:SS]" or an OD datetime ("YYYY-MM-DD HH:MM:SS"); null if unparseable.
 * Copied from src/utils/scheduleView/layout.js (minutesFromMidnight / timeToMinutes) so this file stays
 * import-free. Reads the clock field textually - never constructs a Date.
 */
export function timeToMinutes(odTime) {
  if (typeof odTime !== 'string') return null;
  const m = odTime.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null;
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
  return hours * 60 + mins;
}

/**
 * Collapse overlapping / touching schedule blocks into the time they cover.
 * Sorted by start; a block whose start is <= the running stop is folded in. Idempotent, and it never
 * mutates its input. Unusable entries (non-finite, or stop <= start) are skipped.
 *
 * @param {Array<{start:number,stop:number}>} blocks - minutes from midnight
 * @returns {Array<{start:number,stop:number}>}
 */
export function mergeBlocks(blocks) {
  if (!Array.isArray(blocks)) return [];
  const sorted = blocks
    .filter(b => b && Number.isFinite(b.start) && Number.isFinite(b.stop) && b.stop > b.start)
    .map(b => ({ start: b.start, stop: b.stop }))
    .sort((a, b) => a.start - b.start);
  const merged = [];
  sorted.forEach(b => {
    const last = merged[merged.length - 1];
    if (last && b.start <= last.stop) {
      if (b.stop > last.stop) last.stop = b.stop;
    } else {
      merged.push(b);
    }
  });
  return merged;
}

/**
 * Group appointment rows by day, then by every provider they occupy.
 *
 * @param {Array<Object>} rows - { AptDateTime, Pattern, AptStatus, ProvNum, ProvHyg }
 * @param {function(Object): string} dayOf - the day key for a row (the Vue side passes
 *        row => moment(row.AptDateTime).format('YYYY-MM-DD'))
 * @returns {Object} { day: { provNum: [{ t, dur, status }] } } - t = start minute of day, dur = minutes
 */
export function groupAppointmentsByProviderDay(rows, dayOf) {
  const out = {};
  (rows || []).forEach(row => {
    if (!row) return;
    const t = timeToMinutes(row.AptDateTime);
    if (t === null) return;
    const day = typeof dayOf === 'function' ? dayOf(row) : null;
    if (!day) return;
    const patternLength = row.Pattern ? String(row.Pattern).length : 0;
    const appt = { t, dur: patternLength * 5, status: row.AptStatus };
    appointmentProviderNums(row).forEach(provNum => {
      if (!out[day]) out[day] = {};
      if (!out[day][provNum]) out[day][provNum] = [];
      out[day][provNum].push(appt);
    });
  });
  return out;
}

/**
 * Group provider-schedule rows by day, then by provider, MERGED (see mergeBlocks).
 * Rows with an unreadable time or a non-positive length are dropped before merging.
 *
 * @param {Array<Object>} rows - { ProvNum, SchedDate ('YYYY-MM-DD'), StartTime, StopTime ('HH:MM:SS') }
 * @returns {Object} { 'YYYY-MM-DD': { provNum: [{ start, stop }] } } - minutes from midnight, merged, sorted by start
 */
export function groupScheduleByProviderDay(rows) {
  const out = {};
  (rows || []).forEach(row => {
    if (!row) return;
    const provNum = parseInt(row.ProvNum, 10);
    if (!Number.isFinite(provNum) || provNum <= 0) return;
    const dayMatch = typeof row.SchedDate === 'string' ? row.SchedDate.match(/^\d{4}-\d{2}-\d{2}/) : null;
    if (!dayMatch) return;
    const start = timeToMinutes(row.StartTime);
    const stop = timeToMinutes(row.StopTime);
    if (start === null || stop === null || stop <= start) return;
    const day = dayMatch[0];
    if (!out[day]) out[day] = {};
    if (!out[day][provNum]) out[day][provNum] = [];
    out[day][provNum].push({ start, stop });
  });
  Object.keys(out).forEach(day => {
    Object.keys(out[day]).forEach(provNum => {
      out[day][provNum] = mergeBlocks(out[day][provNum]);
    });
  });
  return out;
}

/**
 * Lunch minutes to deduct for a provider on a day, from Set Goals only.
 * A provider who is listed AND has the weekday ticked gets their LunchMinutes; everyone else gets 0 -
 * there is no default (some providers do not take a lunch; the office sets it per provider per day).
 * The split-schedule gap is handled separately inside computeDayHours (larger of the two wins).
 *
 * @param {Array|null} providerLunchDuration - HUDDLE_GOALS.providerLunchDuration ([{ ProvNum, LunchMinutes, ApplyTo }])
 * @param {string|number} provNum
 * @param {number} isoWeekday - 1 = Mon ... 7 = Sun
 * @returns {number}
 */
export function resolveLunchMinutes(providerLunchDuration, provNum, isoWeekday) {
  const entry = Array.isArray(providerLunchDuration)
    ? providerLunchDuration.find(p => p && String(p.ProvNum) === String(provNum))
    : null;
  if (entry && entry.ApplyTo && entry.ApplyTo[isoWeekday]) return Number(entry.LunchMinutes) || 0;
  return 0;
}

/** Hours the (merged) schedule blocks cover. Overlapping / duplicate blocks count once. */
export function scheduleHours(blocks) {
  return mergeBlocks(blocks).reduce((sum, b) => sum + (b.stop - b.start), 0) / 60;
}

// Minutes inside [from, to] that no appointment interval [t, t + dur] covers.
function uncoveredMinutes(from, to, appts) {
  const covered = appts
    .map(a => ({ start: Math.max(from, a.t), stop: Math.min(to, a.t + (a.dur || 0)) }))
    .filter(iv => iv.stop > iv.start);
  const coveredMinutes = mergeBlocks(covered).reduce((sum, iv) => sum + (iv.stop - iv.start), 0);
  return (to - from) - coveredMinutes;
}

/**
 * Clinical hours for one provider on one day.
 *
 * @param {Object} args
 * @param {Array<{t:number,dur:number}>} args.appts - from groupAppointmentsByProviderDay
 * @param {Array<{start:number,stop:number}>} args.blocks - from groupScheduleByProviderDay (may be empty);
 *        merged again here, so an unmerged list is safe to pass
 * @param {number} args.lunchMinutes - from resolveLunchMinutes
 * @returns {{ hours: number, source: 'schedule'|'appointments'|'none' }}
 */
export function computeDayHours({ appts, blocks, lunchMinutes } = {}) {
  if (!Array.isArray(appts) || appts.length === 0) return { hours: 0, source: 'none' };
  const merged = mergeBlocks(blocks);

  let windowStart = Infinity;
  let windowEnd = -Infinity;
  appts.forEach(a => {
    windowStart = Math.min(windowStart, a.t);
    windowEnd = Math.max(windowEnd, a.t + (a.dur || 0));
  });
  if (merged.length > 0) {
    windowStart = Math.min(windowStart, merged[0].start);
    windowEnd = Math.max(windowEnd, merged[merged.length - 1].stop);
  }

  // The empty stretch between two schedule blocks already IS a lunch; only the part nobody was booked into counts.
  let emptyGap = 0;
  for (let i = 1; i < merged.length; i++) {
    emptyGap += uncoveredMinutes(merged[i - 1].stop, merged[i].start, appts);
  }
  const goalsLunch = Number(lunchMinutes) > 0 ? Number(lunchMinutes) : 0;
  const lunch = Math.max(emptyGap, goalsLunch);

  return {
    hours: Math.max(0, windowEnd - windowStart - lunch) / 60,
    source: merged.length > 0 ? 'schedule' : 'appointments',
  };
}
